export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MILESTONE_YEARS = [1, 2, 3, 5, 10];
export const BANDS = [
    ["Under 6m", 0, 6],
    ["6–12m", 6, 12],
    ["1–2y", 12, 24],
    ["2–5y", 24, 60],
    ["5+y", 60, Infinity],
];

const DAY = 86400000;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const parseDoj = (s) => {
    if (!s) return null;
    const d = new Date(`${String(s).slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
};
const genderOf = (u) => (u.gender === "male" || u.gender === "female" ? u.gender : "unset");
export const monthsBetween = (a, b) =>
    Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth() - (b.getDate() < a.getDate() ? 1 : 0));
export const fmtTenure = (m) => {
    if (m == null) return "—";
    const t = Math.round(m);
    return `${Math.floor(t / 12)}y ${t % 12}m`;
};
const median = (arr) => {
    if (!arr.length) return null;
    const s = [...arr].sort((a, b) => a - b), mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};
const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
const blank = () => ({ total: 0, male: 0, female: 0, unset: 0 });
const add = (b, g) => { b.total += 1; b[g] += 1; return b; };
const pctChange = (cur, prev) => (prev ? Math.round(((cur - prev) * 100) / prev) : null);

// Anniversary date in a given year (Feb 29 joiners celebrate Feb 28 in non-leap years).
const annIn = (doj, y) => {
    const d = new Date(y, doj.getMonth(), doj.getDate());
    return d.getMonth() === doj.getMonth() ? d : new Date(y, doj.getMonth() + 1, 0);
};

export const ratioText = (m, f) => {
    if (!m && !f) return "—";
    if (!f) return `${m} : 0`;
    return `${(m / f).toFixed(2)} : 1`;
};

const workforce = (people, dated) => {
    const counts = people.reduce((b, p) => add(b, p._g), blank());
    const years = {};
    for (const p of dated) add((years[p._doj.getFullYear()] ||= blank()), p._g);
    const byYear = Object.keys(years).sort().map((y) => ({ year: y, ...years[y] }));
    return { counts, byYear, missingDoj: people.length - dated.length };
};

const tenure = (dated) => {
    const months = dated.map((p) => p._tm);
    const bands = BANDS.map(([label, lo, hi]) => dated.filter((p) => p._tm >= lo && p._tm < hi).reduce((b, p) => add(b, p._g), { label, ...blank() }));
    const byGender = ["male", "female"].map((g) => {
        const ms = dated.filter((p) => p._g === g).map((p) => p._tm);
        return { gender: g, count: ms.length, avg: avg(ms), median: median(ms) };
    });
    return { count: dated.length, avg: avg(months), median: median(months), bands, byGender };
};

const hiring = (dated, today) => {
    const key = (d) => `${d.getFullYear()}-${d.getMonth()}`;
    const byMonth = {};
    for (const p of dated) add((byMonth[key(p._doj)] ||= blank()), p._g);
    const series = [];
    for (let i = 12; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        series.push({ label: `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, ...(byMonth[key(d)] || blank()) });
    }
    const monthly = series.slice(1).map((m, i) => ({ ...m, growth: pctChange(m.total, series[i].total) }));

    const quarters = [];
    const curQ = Math.floor(today.getMonth() / 3);
    for (let i = 7; i >= 0; i--) {
        const qd = new Date(today.getFullYear(), (curQ - i) * 3, 1);
        const q = Math.floor(qd.getMonth() / 3), y = qd.getFullYear();
        const total = dated.filter((p) => p._doj.getFullYear() === y && Math.floor(p._doj.getMonth() / 3) === q).length;
        quarters.push({ label: `Q${q + 1} ${String(y).slice(2)}`, total });
    }

    const yearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate() + 1);
    const newJoiners = dated.filter((p) => p._doj >= yearAgo).reduce((b, p) => add(b, p._g), blank());

    const cal = MONTHS.map((m) => ({ month: m, total: 0 }));
    for (const p of dated) cal[p._doj.getMonth()].total += 1;
    const top = [...cal].filter((c) => c.total).sort((a, b) => b.total - a.total).slice(0, 3).map((c) => c.month);
    const peak = cal.map((c) => ({ ...c, peak: top.includes(c.month) }));

    const cur = monthly[11], prev = monthly[10];
    return { monthly, quarters, newJoiners, peak, peakTop: top, mom: { cur: cur.total, prev: prev.total, growth: pctChange(cur.total, prev.total), label: cur.label, prevLabel: prev.label } };
};

const milestones = (dated, today) => {
    const y = today.getFullYear();
    const rows = dated.map((p) => {
        const thisYear = annIn(p._doj, y);
        const next = thisYear < today ? annIn(p._doj, y + 1) : thisYear;
        return { p, thisYear, yearsThisYear: y - p._doj.getFullYear(), next, daysUntil: Math.round((next - today) / DAY), yearsAtNext: next.getFullYear() - p._doj.getFullYear() };
    }).filter((r) => r.yearsAtNext >= 1 || r.yearsThisYear >= 1);

    const todays = rows.filter((r) => r.daysUntil === 0 && r.yearsAtNext >= 1);
    const upcoming = rows.filter((r) => r.daysUntil >= 1 && r.daysUntil <= 7 && r.yearsAtNext >= 1).sort((a, b) => a.daysUntil - b.daysUntil);
    const milestoneRows = rows.filter((r) => MILESTONE_YEARS.includes(r.yearsThisYear)).sort((a, b) => a.thisYear - b.thisYear);
    const byMilestone = MILESTONE_YEARS.map((n) => ({ years: n, label: `${n} yr${n > 1 ? "s" : ""}`, people: milestoneRows.filter((r) => r.yearsThisYear === n) }));
    const genderDist = byMilestone.map((m) => m.people.reduce((b, r) => add(b, r.p._g), { label: m.label, ...blank() }));
    const calendar = MONTHS.map((month, i) => ({ month, items: rows.filter((r) => r.yearsThisYear >= 1 && r.thisYear.getMonth() === i).sort((a, b) => a.thisYear - b.thisYear) }));
    return { todays, upcoming, byMilestone, genderDist, calendar };
};

export const computeWorkforce = (list, now = new Date()) => {
    const today = startOfDay(now);
    const people = list.map((u) => ({ ...u, _doj: parseDoj(u.date_of_joining), _g: genderOf(u) }));
    const dated = people.filter((p) => p._doj && p._doj <= today).map((p) => ({ ...p, _tm: monthsBetween(p._doj, today) }));
    return { today, workforce: workforce(people, dated), tenure: tenure(dated), hiring: hiring(dated, today), milestones: milestones(dated, today) };
};
